/**
 * CalendarToolbar.js — Shared react-big-calendar custom toolbar.
 *
 * Single source of truth for the calendar toolbar design used across all
 * pages (Search / All Programs / My Events). Each page passes a unique
 * `togglePrefix` so the hidden proxy button IDs never clash when multiple
 * calendars exist on the same page simultaneously.
 *
 * Props (injected by react-big-calendar):
 *  - label      {string}    Current period label (e.g. "May 2026").
 *  - onNavigate {Function}  Navigate to PREV / NEXT / TODAY.
 *  - view       {string}    Active view name ("month" | "week" | "day" | "agenda").
 *  - onView     {Function}  Switch to a view.
 *
 * Own props:
 *  - togglePrefix {string}  Prefix for hidden proxy button IDs.
 *                           e.g. "rbc" → ids: rbc-toggle-month, rbc-toggle-week …
 *                           Default: "lc".  Set a unique value per page.
 */

import React from 'react';

export const CalendarToolbar = ({ label, onNavigate, view, onView, togglePrefix = 'lc' }) => {
  const goToBack    = () => onNavigate('PREV');
  const goToNext    = () => onNavigate('NEXT');
  const goToCurrent = () => onNavigate('TODAY');

  const formatDate = (dateString) => {
    const date  = new Date(dateString);
    let month   = '' + (date.getMonth() + 1);
    let day     = '' + date.getDate();
    const year  = date.getFullYear();
    if (month.length < 2) month = '0' + month;
    if (day.length   < 2) day   = '0' + day;
    return [day, month, year].join('/');
  };

  const reformatDateRange = (dateRange) => {
    const dates = dateRange.split(' – ');
    if (dates.length === 2) {
      return formatDate(dates[0]) + ' – ' + formatDate(dates[1]);
    }
    return '';
  };

  const formatLabel = () => {
    if (view === 'agenda') return reformatDateRange(label);
    return label;
  };

  return (
    <div className="rbc-toolbar lc-cal-toolbar">
      {/* ← Prev */}
      <button type="button" className="lc-cal-prev" onClick={goToBack}>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10 3L5 8L10 13" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Centered label + "Return to today" */}
      <div className="lc-cal-center">
        <span className="rbc-toolbar-label lc-cal-label">{formatLabel()}</span>
        <button type="button" className="lc-cal-today-link" onClick={goToCurrent}>Return to today</button>
      </div>

      {/* → Next */}
      <button type="button" className="lc-cal-next" onClick={goToNext}>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M6 3L11 8L6 13" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Hidden proxy buttons — clicked by the external view-toggle pills */}
      <span className="lc-cal-views-hidden" aria-hidden="true">
        <button id={`${togglePrefix}-toggle-month`}  type="button" onClick={() => onView('month')}>Month</button>
        <button id={`${togglePrefix}-toggle-week`}   type="button" onClick={() => onView('week')}>Week</button>
        <button id={`${togglePrefix}-toggle-day`}    type="button" onClick={() => onView('day')}>Day</button>
        <button id={`${togglePrefix}-toggle-agenda`} type="button" onClick={() => onView('agenda')}>Agenda</button>
      </span>
    </div>
  );
};
